import { AuthResponse, SpeakerEvent, SSOAuthResponse } from '../../types';
import { DateTime } from 'luxon';
import { ACCESS_TOKEN_EXPIRATION_BUFFER } from '../apiConstants';
import { GQLEditorSegment } from '@upheal/graphql/definitions/schema.generated';
import { captureException } from '../sentry';
import { LAST_SIGNED_IN_KEY } from '../useLastSignedIn';

export interface ISessionSettings {
    id: string;
    progressNoteSeen: boolean;
}

type AuthTokenInput = (AuthResponse | SSOAuthResponse) & {
    isClient?: boolean;
    otp?: string;
    rememberMe?: boolean;
};

export type AuthTokenOutput = AuthTokenInput & {
    accessTokenExpiresAt?: number;
};

export const browserStorage = {
    getCurrentVersion(): string {
        // last version we had was '1'
        return localStorage.getItem('BROWSER_STORAGE/version') || '1';
    },

    /**
     * Notes
     */
    setNotesEntry(notes: Map<string, GQLEditorSegment[]>) {
        localStorage.setItem(
            'BROWSER_STORAGE/notes',
            JSON.stringify(Array.from(notes)),
        );
    },
    getNotesEntry(): Map<string, GQLEditorSegment[]> {
        const notesEntry = localStorage.getItem('BROWSER_STORAGE/notes');

        if (!notesEntry) {
            return new Map();
        }

        return new Map(JSON.parse(notesEntry) as any);
    },

    saveNotes(providerId: string, segments: GQLEditorSegment[]) {
        const notesEntry = browserStorage.getNotesEntry();
        notesEntry.set(providerId, segments);
        browserStorage.setNotesEntry(notesEntry);
    },
    loadNotes(providerId: string): GQLEditorSegment[] {
        const notesEntry = browserStorage.getNotesEntry();
        return notesEntry.get(providerId) ?? [];
    },
    clearNotes(providerId: string) {
        const notesEntry = browserStorage.getNotesEntry();
        notesEntry.delete(providerId);
        browserStorage.setNotesEntry(notesEntry);
    },

    /**
     * Tokens
     */
    saveAuthToken(token: AuthTokenInput | null) {
        if (!token) {
            return;
        }

        let accessTokenExpiresAt;
        try {
            accessTokenExpiresAt = DateTime.now()
                .plus({
                    millisecond:
                        token.expiresInSeconds * 1000 -
                        ACCESS_TOKEN_EXPIRATION_BUFFER,
                })
                .toMillis();
        } catch (error) {
            const tokenForSentry: Partial<AuthTokenInput> = { ...token };
            delete tokenForSentry.accessToken;

            captureException('Failed to parse token expiration time.', {
                token: tokenForSentry,
                error,
            });
        }

        const tokenToSave: AuthTokenOutput = {
            ...token,
            accessTokenExpiresAt,
        };

        localStorage.setItem(
            'BROWSER_STORAGE/authToken',
            JSON.stringify(tokenToSave),
        );
    },
    loadAuthToken(): AuthTokenOutput | null {
        return JSON.parse(
            localStorage.getItem('BROWSER_STORAGE/authToken') || 'null',
        ) as any;
    },
    clearAuthToken() {
        localStorage.removeItem('BROWSER_STORAGE/authToken');
    },

    /**
     * Session Settings
     */
    setSessionSettingsEntry(sessionSettings: Map<string, ISessionSettings>) {
        localStorage.setItem(
            'BROWSER_STORAGE/sessionSettings',
            JSON.stringify(Array.from(sessionSettings)),
        );
    },
    getSessionSettingsEntry(): Map<string, ISessionSettings> {
        const sessionSettingsEntry = localStorage.getItem(
            'BROWSER_STORAGE/sessionSettings',
        );

        if (!sessionSettingsEntry) {
            return new Map();
        }

        return new Map(JSON.parse(sessionSettingsEntry) as any);
    },

    setSpeakerEventsEntry(speakerEvents: Map<string, Array<SpeakerEvent>>) {
        localStorage.setItem(
            'BROWSER_STORAGE/speakerEvents',
            JSON.stringify(Array.from(speakerEvents)),
        );
    },
    getSpeakerEventsEntry(): Map<string, Array<SpeakerEvent>> {
        const speakerEventsFromLocalStorage = localStorage.getItem(
            'BROWSER_STORAGE/speakerEvents',
        );

        if (!speakerEventsFromLocalStorage) {
            return new Map();
        }

        return new Map(JSON.parse(speakerEventsFromLocalStorage) as any);
    },

    saveSpeakerEvent(sessionId: string, speakerEvent: SpeakerEvent) {
        const speakerEventsForSession =
            browserStorage.loadSpeakerEvents(sessionId);
        speakerEventsForSession.push(speakerEvent);
        const speakerEventsEntry = browserStorage.getSpeakerEventsEntry();
        speakerEventsEntry.set(sessionId, speakerEventsForSession);
        browserStorage.setSpeakerEventsEntry(speakerEventsEntry);

        return speakerEventsForSession;
    },
    loadSpeakerEvents(sessionId: string): Array<SpeakerEvent> {
        return browserStorage.getSpeakerEventsEntry().get(sessionId) ?? [];
    },
    clearSpeakerEvents(sessionId: string) {
        const speakerEventsEntry = browserStorage.getSpeakerEventsEntry();
        speakerEventsEntry.delete(sessionId);
        browserStorage.setSpeakerEventsEntry(speakerEventsEntry);
    },

    getNotShowConsentCollectionSkipConfirmation(): boolean {
        return (
            localStorage.getItem(
                'BROWSER_STORAGE/notShowConsentCollectionSkipConfirmation',
            ) === 'true'
        );
    },
    setNotShowConsentCollectionSkipConfirmation(notShow: boolean) {
        localStorage.setItem(
            'BROWSER_STORAGE/notShowConsentCollectionSkipConfirmation',
            notShow.toString(),
        );
    },

    getExtensionWidgetPosition(): { x: number; y: number } | null {
        const storedPosition = localStorage.getItem(
            'BROWSER_STORAGE/extensionWidgetPosition',
        );

        return storedPosition ? (JSON.parse(storedPosition) as any) : null;
    },

    setExtensionWidgetPosition(position: { x: number; y: number }) {
        localStorage.setItem(
            'BROWSER_STORAGE/extensionWidgetPosition',
            JSON.stringify(position),
        );
    },

    getExtensionHadFirstSignIn() {
        return (
            localStorage.getItem('BROWSER_STORAGE/extensionFirstSignIn') ===
            'true'
        );
    },

    setExtensionHadFirstSignIn() {
        localStorage.setItem(
            'BROWSER_STORAGE/extensionFirstSignIn',
            true.toString(),
        );
    },

    getSessionRecordingsDatabaseErrorsCount() {
        return parseInt(
            localStorage.getItem('SessionRecordingsDatabase/errorsCount') ??
                '0',
            10,
        );
    },

    incrementSessionRecordingsDatabaseErrorsCount() {
        let counter = browserStorage.getSessionRecordingsDatabaseErrorsCount();
        counter += 1;

        localStorage.setItem(
            'SessionRecordingsDatabase/errorsCount',
            counter.toString(),
        );
    },

    resetSessionRecordingsDatabaseErrorsCount() {
        localStorage.setItem('SessionRecordingsDatabase/errorsCount', '0');
    },

    setLastSignedIn() {
        localStorage.setItem(LAST_SIGNED_IN_KEY, DateTime.now().toISO());
    },

    getLastSignedIn() {
        const lastSignedIn = localStorage.getItem(LAST_SIGNED_IN_KEY);

        return lastSignedIn ? DateTime.fromISO(lastSignedIn) : undefined;
    },
};
